import GameLog from "../classes/GameLog.js";
import Pad from "../classes/Pad.js";
import FrameStore from "../classes/FrameStore.js";
import GrimoireTimeline from "../classes/GrimoireTimeline.js";
import TokenStore from "../classes/TokenStore.js";
import {
    gameObserver,
    logObserver,
    mainStore
} from "../constants/observers.js";
import {
    lookupOne,
    lookupOneCached
} from "../utils/elements.js";
import {
    escapeHtml
} from "../utils/strings.js";
import {
    uploadFormData
} from "../utils/fetch.js";
import {
    encodeFrames,
    isSupported as isVideoEncodingSupported
} from "../utils/video-encoder.js";
import {
    initTokenAnimations
} from "../utils/token-animations.js";

const KNOWN_DEATH_REASONS = ["execution", "demon", "other"];

const gameLog = new GameLog(mainStore);

const panelElement = lookupOneCached(".js--game-log-panel");
const entriesListElement = lookupOne(".js--game-log-entries");
const formElement = lookupOne(".js--game-log-form");
const typeSelectElement = lookupOne(".js--game-log-type");
const editingIdElement = lookupOne(".js--game-log-editing-id");
const phaseIndicatorElement = lookupOne(".js--phase-indicator");
const padElement = lookupOne(".js--pad");
const playerSelectElement = lookupOneCached(".js--game-log-player");
const noteElement = lookupOneCached(".js--game-log-note");
const timeline = new GrimoireTimeline(
    lookupOne(".js--pad-wrapper"),
    FrameStore.isSupported() ? new FrameStore() : null
);

let phaseChangePending = false;
let gameStartPending = false;

const updateStartButton = () => {
    const button = lookupOne(".js--game-log-start");
    if (button) {
        button.disabled = gameStartPending || Boolean(gameLog.data.startedAt && !gameLog.data.endedAt);
    }
};

const getPlayerNames = () => gameLog.getPlayerNames();

const getPhaseLabel = (day, phase) => {
    if (!day || !phase) {
        return "";
    }
    return `${phase === "night" ? "Ночь" : "День"} ${day}`;
};

const captureCurrentPhase = () => timeline.capture(
    getPhaseLabel(gameLog.data.currentDay, gameLog.data.currentPhase)
);

const renderPhaseIndicator = () => {
    updateStartButton();
    const label = getPhaseLabel(gameLog.data.currentDay, gameLog.data.currentPhase);

    if (padElement) {
        padElement.dataset.phase = label ? gameLog.data.currentPhase : "";
    }

    if (!phaseIndicatorElement) {
        return;
    }

    phaseIndicatorElement.textContent = label;
    phaseIndicatorElement.hidden = !label;
};

const updatePlayerSelects = () => {

    const names = getPlayerNames();
    const selects = document.querySelectorAll(".js--game-log-player-select");

    selects.forEach((select) => {

        const currentValue = select.value;
        select.innerHTML = "<option value=\"\">—</option>";

        gameLog.data.players.filter((player) => player.playerName).forEach((player) => {
            const option = document.createElement("option");
            option.value = player.playerName;
            option.dataset.characterId = player.characterId || "";
            option.textContent = names.filter((name) => name === player.playerName).length > 1
                ? `${player.playerName} — ${player.characterName || player.characterId}`
                : player.playerName;
            select.append(option);
        });

        if (names.includes(currentValue)) {
            select.value = currentValue;
        }

    });

};

const runTokenAnimation = initTokenAnimations(timeline);

const renderEntries = () => {

    if (!entriesListElement) {
        return;
    }

    const entries = gameLog.getEntries();

    entriesListElement.innerHTML = "";

    if (!entries.length) {
        entriesListElement.innerHTML = `<li class="game-log__empty">${I18N.gameLogNoEntries || "Нет записей"}</li>`;
        return;
    }

    entries.forEach((entry, index) => {

        const item = document.createElement("li");
        item.className = "game-log__entry";
        item.dataset.id = entry.id;

        const text = gameLog.formatEntry(entry);
        const meta = [];

        if (entry.day) {
            meta.push(`Д${entry.day}`);
        }
        if (entry.phase) {
            meta.push(entry.phase === "night" ? "ночь" : "день");
        }

        item.innerHTML = `
            <span class="game-log__entry-order">
                <button type="button" class="no-btn game-log__entry-move" data-id="${entry.id}" data-direction="up" title="Переместить вверх" aria-label="Переместить вверх" ${index === 0 ? "disabled" : ""}>↑</button>
                <button type="button" class="no-btn game-log__entry-move" data-id="${entry.id}" data-direction="down" title="Переместить вниз" aria-label="Переместить вниз" ${index === entries.length - 1 ? "disabled" : ""}>↓</button>
            </span>
            <span class="game-log__entry-text">${escapeHtml(text)}</span>
            ${meta.length ? `<span class="game-log__entry-meta">${escapeHtml(meta.join(" · "))}</span>` : ""}
            <span class="game-log__entry-actions">
                <button type="button" class="no-btn game-log__entry-edit" data-id="${entry.id}" title="Редактировать">✏️</button>
                <button type="button" class="no-btn game-log__entry-delete" data-id="${entry.id}" title="Удалить">🗑️</button>
            </span>
        `;

        entriesListElement.append(item);

    });

};

const updateReasonCustomField = () => {

    const reasonSelect = lookupOne(".js--game-log-reason");
    const customField = lookupOne(`.js--game-log-field[data-field="reason-custom"]`);

    if (!reasonSelect || !customField) {
        return;
    }

    customField.hidden = reasonSelect.value !== "other";

};

const showFormFields = (type) => {

    document
        .querySelectorAll(".js--game-log-field")
        .forEach((field) => field.hidden = true);

    const fields = {
        "phase": ["day", "phase"],
        "execution": ["player"],
        "death": ["player", "reason"],
        "note": ["note-text"]
    };

    (fields[type] || []).forEach((name) => {
        const field = lookupOne(`.js--game-log-field[data-field="${name}"]`);
        if (field) {
            field.hidden = false;
        }
    });

    if (type === "death") {
        updateReasonCustomField();
    }

};

const resetForm = () => {

    if (formElement) {
        formElement.reset();
    }

    if (editingIdElement) {
        editingIdElement.value = "";
    }

    showFormFields(typeSelectElement ? typeSelectElement.value : "phase");
    updatePlayerSelects();

};

const populateForm = (entry) => {

    if (!formElement || !typeSelectElement) {
        return;
    }

    typeSelectElement.value = entry.type;
    editingIdElement.value = entry.id;
    showFormFields(entry.type);

    const data = entry.data;

    switch (entry.type) {

    case "phase":
        lookupOne(".js--game-log-day").value = data.day || "";
        lookupOne(".js--game-log-phase").value = data.phase || "day";
        break;

    case "execution":
    case "death":
        playerSelectElement.value = data.playerName || "";
        if (data.characterId) {
            const option = [...playerSelectElement.options].find((item) => (
                item.value === data.playerName && item.dataset.characterId === data.characterId
            ));
            if (option) {
                option.selected = true;
            }
        }
        if (entry.type === "death") {
            const isKnown = KNOWN_DEATH_REASONS.includes(data.reason);
            lookupOne(".js--game-log-reason").value = isKnown ? data.reason : "other";
            lookupOne(".js--game-log-reason-custom").value = isKnown
                ? (data.reasonCustom || "")
                : (data.reason || "");
            updateReasonCustomField();
        }
        break;

    case "note":
        lookupOne(".js--game-log-note-text").value = data.text || entry.note || "";
        break;

    default:
        break;

    }

    if (noteElement) {
        noteElement.value = entry.note || "";
    }

};

const handleFormSubmit = async (event) => {

    event.preventDefault();

    if (!formElement || !typeSelectElement) {
        return;
    }

    const type = typeSelectElement.value;
    const editingId = editingIdElement ? editingIdElement.value : "";
    const note = noteElement?.value.trim() || "";
    let data = {};

    switch (type) {

    case "phase":
        data = {
            day: parseInt(lookupOne(".js--game-log-day").value, 10) || 1,
            phase: lookupOne(".js--game-log-phase").value
        };
        break;

    case "execution":
        data = {
            playerName: playerSelectElement.value.trim()
        };
        break;

    case "death":
        data = {
            playerName: playerSelectElement.value.trim(),
            reason: lookupOne(".js--game-log-reason").value,
            reasonCustom: lookupOne(".js--game-log-reason-custom")?.value.trim() || ""
        };
        break;

    case "note":
        data = {
            text: lookupOne(".js--game-log-note-text").value.trim()
        };
        break;

    default:
        return;

    }

    if (["death", "execution"].includes(type)) {
        data.characterId = playerSelectElement.selectedOptions[0]?.dataset.characterId || "";
    }

    if (editingId) {
        gameLog.updateEntry(editingId, {
            data,
            note
        });
        logObserver.trigger("log-entry-updated");
    } else {
        if (type === "phase") {
            await captureCurrentPhase();
        }
        gameLog.addEntry(type, data, note);
        logObserver.trigger("log-entry-added");

        if (type === "execution") {
            const info = padElement.pad.characters.find((item) => item.character.getId() === data.characterId);
            runTokenAnimation(info ? Pad.getToken(info.token) : null, "execution");
        }
    }

    resetForm();
    renderEntries();
    renderPhaseIndicator();

};

const handleEntryAction = (event) => {

    const button = event.target.closest("[data-id]");

    if (!button) {
        return;
    }

    const id = button.dataset.id;

    if (button.classList.contains("game-log__entry-move")) {
        const fromIndex = gameLog.getEntries().findIndex((entry) => entry.id === id);
        const direction = button.dataset.direction;
        const toIndex = fromIndex + (direction === "up" ? -1 : 1);

        if (gameLog.moveEntry(fromIndex, toIndex)) {
            renderEntries();
            const buttons = Array.from(entriesListElement.querySelectorAll(".game-log__entry-move"))
                .filter((item) => item.dataset.id === id);
            const focusButton = buttons.find((item) => item.dataset.direction === direction && !item.disabled)
                || buttons.find((item) => !item.disabled);
            focusButton?.focus();
        }
        return;
    }

    if (button.classList.contains("game-log__entry-delete")) {
        gameLog.deleteEntry(id);
        logObserver.trigger("log-entry-deleted");
        renderEntries();
        return;
    }

    if (button.classList.contains("game-log__entry-edit")) {
        const entry = gameLog.getEntries().find((item) => item.id === id);
        if (entry) {
            populateForm(entry);
        }
    }

};

const handleStartGame = async () => {

    if (gameStartPending || (gameLog.data.startedAt && !gameLog.data.endedAt)) {
        return;
    }

    gameStartPending = true;
    updateStartButton();
    let tokenStore;
    try {
        tokenStore = await TokenStore.get();
    } finally {
        gameStartPending = false;
        updateStartButton();
    }

    const storeData = mainStore.get();
    const characters = storeData.characters || {};
    const meta = characters.meta || {};
    const scriptName = meta.name || meta.title || "Custom Script";
    const scriptId = characters.game || meta.name || "custom";

    const players = storeData.tokens
        .filter((token) => token.playerName)
        .map((token) => {
            const character = tokenStore.getCharacter(token.id);
            return {
                characterId: token.id,
                characterName: character ? character.getName() : token.id,
                playerName: token.playerName,
                team: character ? character.getTeam() : ""
            };
        });

    timeline.clear();
    gameLog.startGame(scriptName, scriptId, players);
    gameLog.addEntry("phase", {
        day: 1,
        phase: "night"
    });
    logObserver.trigger("game-started");
    updatePlayerSelects();
    renderEntries();
    renderPhaseIndicator();

};

const handleNextPhase = async (event) => {

    const logData = gameLog.data;
    const currentDay = gameLog.data.currentDay;
    const currentPhase = gameLog.data.currentPhase;

    if (!currentDay || !currentPhase || phaseChangePending) {
        return;
    }

    const button = event.currentTarget;
    phaseChangePending = true;
    button.disabled = true;

    try {
        await captureCurrentPhase();
        if (gameLog.data !== logData) {
            return;
        }
        gameLog.addEntry("phase", currentPhase === "night" ? {
            day: currentDay,
            phase: "day"
        } : {
            day: currentDay + 1,
            phase: "night"
        });
        logObserver.trigger("log-entry-added");
        renderEntries();
        renderPhaseIndicator();
    } finally {
        phaseChangePending = false;
        button.disabled = false;
    }

};

const handleEndGame = async () => {

    const winner = lookupOne(".js--game-log-winner")?.value || "none";
    const note = lookupOne(".js--game-log-end-note")?.value.trim() || "";

    gameLog.endGame(winner, note);
    await captureCurrentPhase();
    logObserver.trigger("game-ended");
    updateStartButton();
    renderEntries();

};

const handleSendToTelegram = async () => {

    const sendButton = lookupOne(".js--game-log-send");
    const originalLabel = sendButton?.textContent;

    if (sendButton) {
        sendButton.disabled = true;
        sendButton.textContent = "Подготовка…";
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));

    try {
        const text = gameLog.formatText(true);
        const url = URLS.logSend;
        const formData = new FormData();
        const frames = await timeline.createFrames();
        const fastFrameIndexes = timeline.getFastFrameIndexes();

        formData.append("text", text);

        let encoded = false;
        if (frames.length && isVideoEncodingSupported()) {
            try {
                const video = await encodeFrames(frames, fastFrameIndexes);
                formData.append("video", video, "grimoire.mp4");
                encoded = true;
            } catch (error) {
                console.warn("Unable to encode grimoire video in the browser", error);
            }
        }

        if (!encoded) {
            frames.forEach((frame, index) => {
                formData.append("frames[]", frame, `grimoire-${index}.jpg`);
                formData.append("frameFast[]", fastFrameIndexes.has(index) ? "1" : "0");
            });
        }

        console.info(
            `Sending log: ${encoded ? "encoded video" : `${frames.length} frames`}, `
            + `${fastFrameIndexes.size} fast`
        );

        const response = await uploadFormData(url, formData, (event) => {

            if (!sendButton) {
                return;
            }

            if (event.lengthComputable && event.loaded < event.total) {
                sendButton.textContent = `Отправка… ${Math.round(event.loaded / event.total * 100)}%`;
            } else {
                sendButton.textContent = "Обработка…";
            }

        });

        const raw = await response.text();
        let result;

        try {
            result = JSON.parse(raw);
        } catch (parseError) {
            result = {
                success: false,
                message: raw.slice(0, 200) || `${response.status} ${response.statusText}`
            };
        }

        if (result.success) {
            alert(I18N.gameLogSent || "Лог отправлен в Telegram");
        } else {
            alert(result.message || I18N.gameLogSendError || "Не удалось отправить лог");
        }

    } catch (error) {
        alert(I18N.gameLogSendError || "Не удалось отправить лог");
    } finally {
        if (sendButton) {
            sendButton.disabled = false;
            sendButton.textContent = originalLabel;
        }
    }

};

const bindEvents = () => {

    if (formElement) {
        formElement.addEventListener("submit", handleFormSubmit);
    }

    if (typeSelectElement) {
        typeSelectElement.addEventListener("change", () => {
            showFormFields(typeSelectElement.value);
        });
    }

    const reasonSelectElement = lookupOne(".js--game-log-reason");
    if (reasonSelectElement) {
        reasonSelectElement.addEventListener("change", updateReasonCustomField);
    }

    if (entriesListElement) {
        entriesListElement.addEventListener("click", handleEntryAction);
    }

    const startButton = lookupOne(".js--game-log-start");
    if (startButton) {
        startButton.addEventListener("click", handleStartGame);
    }

    const nextPhaseButton = lookupOne(".js--game-log-next-phase");
    if (nextPhaseButton) {
        nextPhaseButton.addEventListener("click", handleNextPhase);
    }

    const endButton = lookupOne(".js--game-log-end");
    if (endButton) {
        endButton.addEventListener("click", handleEndGame);
    }

    const sendButton = lookupOne(".js--game-log-send");
    if (sendButton) {
        sendButton.addEventListener("click", handleSendToTelegram);
    }

    const cancelEditButton = lookupOne(".js--game-log-cancel-edit");
    if (cancelEditButton) {
        cancelEditButton.addEventListener("click", resetForm);
    }

    gameObserver.on("clear", () => {
        gameLog.clear();
        timeline.clear();
        updatePlayerSelects();
        renderEntries();
        renderPhaseIndicator();
    });

    logObserver.on("ability-used", ({ detail }) => {
        gameLog.addEntry("note", { text: detail.text });
        renderEntries();
    });

    logObserver.on("character-died", ({ detail }) => {
        const reason = KNOWN_DEATH_REASONS.includes(detail.reason) ? detail.reason : "other";
        gameLog.addEntry("death", {
            playerName: detail.playerName,
            characterId: detail.characterId || "",
            reason,
            reasonCustom: reason === "other" ? (detail.reasonCustom || detail.reason || "") : ""
        });
        renderEntries();
    });

    const cacheForm = lookupOne("#cache-form");
    if (cacheForm) {
        cacheForm.addEventListener("submit", () => {
            gameLog.reload();
            updatePlayerSelects();
            renderEntries();
            renderPhaseIndicator();
        });
    }

};

const init = async () => {

    if (!panelElement) {
        return;
    }

    await timeline.restore();
    bindEvents();
    showFormFields(typeSelectElement ? typeSelectElement.value : "phase");
    updatePlayerSelects();
    renderEntries();
    renderPhaseIndicator();

};

init();
